import React, { useEffect, useRef, useState } from "react";
import { Circle, Marker, Popup, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";

// Zone polygons draw on a shared canvas — dozens of SVG polygons with
// thousands of vertices make zooming choppy. Focused zones fall back to SVG
// so the CSS highlight animation still works.
const evacCanvas = L.canvas({ padding: 0.5 });

export const EVAC_STATUS_META = {
  active: { label: "Currently Active", color: "#22C55E" },
  reduced: { label: "Reduced", color: "#F59E0B" },
  rescinded: { label: "Rescinded", color: "#979F91" },
  historical: { label: "Historical", color: "#6B7280" },
  not_confirmed: { label: "Status Not Confirmed", color: "#979F91" },
};

export const evacStatusMeta = (a) =>
  EVAC_STATUS_META[a?.status] || EVAC_STATUS_META.not_confirmed;

export const EVAC_LEVEL_META = {
  1: { color: "#22C55E", label: "LEVEL 1 · GET READY" },
  2: { color: "#FF8F1F", label: "LEVEL 2 · GET SET" },
  3: { color: "#E63946", label: "LEVEL 3 · GO NOW" },
};

// Level-1 evac color (kept in sync with EVAC_LEVEL_META[1]).
const LEVEL1_GREEN = "#22C55E";
const RING_MS = 45 * 60e3;

// Orange blinking ring for 45 min after an evac is posted or updated
function isFresh(a) {
  const t = a.updated_at || a.created_at;
  if (!t) return false;
  const age = Date.now() - new Date(t).getTime();
  return age >= 0 && age < RING_MS;
}

let _evacRingIcon = null;
function evacRingIcon() {
  if (!_evacRingIcon) {
    _evacRingIcon = L.divIcon({
      className: "ring-overlay",
      html: '<span class="fire-lifecycle fire-ring-orange"></span>',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  }
  return _evacRingIcon;
}

// Invisible hit-target used as a fallback popup trigger for zones that don't
// currently show a label pill (below zoom 10 or deduped cluster peers).
// Zero visual footprint; click radius comes from Leaflet's marker hit-box.
const invisibleHitIcon = L.divIcon({
  className: "evac-hit-target",
  html: '<span style="display:block;width:14px;height:14px"></span>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const fmtDT = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// "Level 3 — Little Giant - Level 3 (Go Now!) (Chelan County)" -> "Little Giant"
// "A1330 — Okanogan County, Washington" -> "A1330"
export function zoneShortName(title) {
  let t = title || "";
  const parts = t.split("—");
  t = /^\s*level\s*\d\s*$/i.test(parts[0]) ? parts.slice(1).join("—") : parts[0];
  t = t.replace(/\(.*?\)/g, "");
  t = t.replace(/level\s*\d\s*[:\-–]?\s*(go now!?|be ready!?|get set!?|get ready!?)?/gi, "");
  t = t.replace(/^\s*zone\s+/i, "");
  t = t.replace(/\s{2,}/g, " ").trim().replace(/[-–:,\s]+$/, "");
  if (t.length > 24) t = t.slice(0, 22).trimEnd() + "…";
  return t;
}

const escHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function zoneLabelIcon(a) {
  const meta = EVAC_LEVEL_META[a.level] || EVAC_LEVEL_META[1];
  const name = zoneShortName(a.title);
  if (!name) return null;
  return L.divIcon({
    className: "evac-zone-label-wrap",
    html: `<span class="evac-zone-label" style="background:${meta.color};color:#fff">${escHtml(name)}</span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

function EvacPopup({ a, meta }) {
  return (
    <Popup>
      <div style={{ minWidth: 190 }} data-testid={`evac-popup-${a.id}`}>
        <div style={{ fontWeight: 700, color: meta.color, fontSize: 12 }}>
          {meta.label}
        </div>
        <div
          data-testid={`evac-status-${a.id}`}
          style={{
            fontWeight: 700,
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: evacStatusMeta(a).color,
          }}
        >
          {evacStatusMeta(a).label}
        </div>
        <div style={{ fontWeight: 700, fontSize: 13, margin: "3px 0" }}>{a.title}</div>
        {a.description && (
          <div style={{ fontSize: 12, marginBottom: 4 }}>{a.description}</div>
        )}
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          ~{a.radius_mi} mi radius · posted by {a.created_by || "staff"}
        </div>
        {a.created_at && (
          <div style={{ fontSize: 11, opacity: 0.7 }} data-testid={`evac-popup-time-${a.id}`}>
            Posted {fmtDT(a.created_at)}
            {a.updated_at && new Date(a.updated_at) - new Date(a.created_at) > 60000
              ? ` · Updated ${fmtDT(a.updated_at)}`
              : ""}
          </div>
        )}
        {a.source_url && (
          <a href={a.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
            Official source →
          </a>
        )}
      </div>
    </Popup>
  );
}


export default function SheriffEvacLayer({ alerts = [], dedupeMarkers = false, subtle = false, focusEvac = null, onPopupEvac }) {
  const map = useMap();
  const markerRefs = useRef({});
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on("zoomend", onZoom);
    return () => map.off("zoomend", onZoom);
  }, [map]);
  const showLabels = zoom >= 10;

  useEffect(() => {
    if (!focusEvac?.id) return;
    const a = alerts.find((x) => x.id === focusEvac.id);
    if (!a || a.latitude == null) return;
    map.flyTo([a.latitude, a.longitude], Math.max(map.getZoom(), 11), { duration: 0.8 });
    // Two passes: the first opens the popup mid-flight; on zoomend the
    // fallback marker can swap for the label pill (unmount closes the popup),
    // so a second pass re-opens it on whichever marker now holds the ref.
    const t1 = setTimeout(() => markerRefs.current[focusEvac.id]?.openPopup(), 900);
    const t2 = setTimeout(() => markerRefs.current[focusEvac.id]?.openPopup(), 2100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusEvac, alerts, map]);

  // Clicking the zone number (or polygon) highlights the zone
  const [clicked, setClicked] = useState(null);
  const eff = [clicked, focusEvac].filter(Boolean).sort((a, b) => b.ts - a.ts)[0] || null;

  // Compact / dedupe mode: only keep the label pill for the highest-level zone
  // in overlapping clusters. Polygons still render for every zone so the
  // shaded footprint stays accurate.
  let markerIds = null;
  if (dedupeMarkers) {
    const kept = [];
    [...alerts]
      .sort((a, b) => (b.level || 1) - (a.level || 1))
      .forEach((a) => {
        const near = kept.some(
          (k) =>
            Math.abs(k.latitude - a.latitude) < 0.035 &&
            Math.abs(k.longitude - a.longitude) < 0.05,
        );
        if (!near) kept.push(a);
      });
    markerIds = new Set(kept.map((k) => k.id));
  }
  return (
    <>
      {alerts.map((a) => {
        const meta = EVAC_LEVEL_META[a.level] || EVAC_LEVEL_META[1];
        const focused = a.id === eff?.id;
        // Zone label is shown when: dedupe allows this alert, OR it's focused.
        const showLabelPill = !markerIds || markerIds.has(a.id) || focused;
        // At-large marker: an invisible, popup-bearing pin at the zone
        // centroid used as a fallback trigger for zones without a visible
        // label pill (e.g. below zoom 10, or a deduped cluster peer). This
        // lets any click on the polygon still open the popup.
        const needsFallbackMarker = !showLabels || !showLabelPill;
        const openZonePopup = () => {
          setClicked({ id: a.id, ts: Date.now() });
          markerRefs.current[a.id]?.openPopup();
        };
        return (
          <React.Fragment key={a.id}>
            {a.geometry ? (
              <GeoJSON
                key={`${a.id}-L${a.level}-${focused ? eff.ts : "n"}`}
                data={{ type: "Feature", geometry: a.geometry }}
                renderer={focused ? undefined : evacCanvas}
                style={{
                  color: meta.color,
                  weight: focused ? 4 : subtle ? 1.2 : 2,
                  fillColor: meta.color,
                  fillOpacity: focused
                    ? 0.35
                    : subtle ? (a.level === 3 ? 0.07 : 0.03) : a.level === 3 ? 0.22 : 0.12,
                  className: focused ? "evac-focus-highlight" : "",
                }}
                eventHandlers={{ click: openZonePopup }}
              />
            ) : (
              <Circle
                key={`${a.id}-c-${focused ? eff.ts : "n"}`}
                center={[a.latitude, a.longitude]}
                radius={a.radius_mi * 1609.34}
                renderer={focused ? undefined : evacCanvas}
                pathOptions={{
                  color: meta.color,
                  weight: focused ? 4 : subtle ? 1.2 : 2,
                  dashArray: focused || a.level === 3 ? null : "6 4",
                  fillColor: meta.color,
                  fillOpacity: focused
                    ? 0.35
                    : subtle ? (a.level === 3 ? 0.06 : 0.03) : a.level === 3 ? 0.18 : 0.1,
                  className: focused ? "evac-focus-highlight" : "",
                }}
                eventHandlers={{ click: openZonePopup }}
              />
            )}
            {focused && a.geometry && a.radius_mi > 0 && (
              <Circle
                key={`${a.id}-r-${eff.ts}`}
                center={[a.latitude, a.longitude]}
                radius={a.radius_mi * 1609.34}
                pathOptions={{
                  color: meta.color,
                  weight: 2.5,
                  dashArray: "6 6",
                  fill: false,
                  className: "evac-radius-ring",
                }}
              />
            )}
            {/* Freshness pulse: still shown briefly for newly-posted zones. */}
            {showLabelPill && isFresh(a) && (
              <Marker
                position={[a.latitude, a.longitude]}
                icon={evacRingIcon()}
                interactive={false}
                zIndexOffset={-50}
              />
            )}
            {/* Primary trigger: the zone-number pill IS the popup opener. */}
            {showLabels && showLabelPill && zoneLabelIcon(a) && (
              <Marker
                position={[a.latitude, a.longitude]}
                icon={zoneLabelIcon(a)}
                zIndexOffset={100}
                ref={(m) => { markerRefs.current[a.id] = m; }}
                eventHandlers={{
                  click: () => setClicked({ id: a.id, ts: Date.now() }),
                  popupopen: () => onPopupEvac?.(a),
                  popupclose: () => onPopupEvac?.(null),
                }}
              >
                <EvacPopup a={a} meta={meta} />
              </Marker>
            )}
            {/* Fallback trigger: an invisible marker so the popup is still
                reachable at low zoom / on deduped peers. It also carries the
                ref so `focusEvac` fly-to-popup still works below zoom 10. */}
            {needsFallbackMarker && a.latitude != null && (
              <Marker
                position={[a.latitude, a.longitude]}
                icon={invisibleHitIcon}
                zIndexOffset={50}
                ref={(m) => { markerRefs.current[a.id] = m; }}
                eventHandlers={{
                  click: () => setClicked({ id: a.id, ts: Date.now() }),
                  popupopen: () => onPopupEvac?.(a),
                  popupclose: () => onPopupEvac?.(null),
                }}
              >
                <EvacPopup a={a} meta={meta} />
              </Marker>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
